import React, { useEffect, useState } from 'react'
import { Typography, Paper, Box, Grid, Card, CardContent, TextField, InputAdornment, IconButton, Select, MenuItem, FormControl, InputLabel, Button, Drawer, List, ListItem, ListItemText, ListItemIcon, Checkbox, Divider, Chip, Tabs, Tab, Tooltip } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'
import ChatIcon from '@mui/icons-material/Chat'
import CallIcon from '@mui/icons-material/Call'
import InfoIcon from '@mui/icons-material/Info'
import useFullPageLoader from '../../hooks/useFullPageLoader'
import { toastMessage } from '../../utils/toastMessage'
import { fetchUserInsightsApi } from '../../services/insightManagement'
import NotFound from '../../components/not-found/NotFound'
import Pagination from 'react-js-pagination'
import { formatToTwoDecimalPoints } from '../../utils/dateFormat'
import { Link } from 'react-router-dom'
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import moment from 'moment'
import { fatchCategoryApi } from '../../services/categoryManagement'


const UserInsight = () => {
  const [userList, setUserList] = useState([])
  const [total, setTotal] = useState(0)
  const [activePage, setActivePage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [searchQuery, setSearchQuery] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [loader, showLoader, hideLoader] = useFullPageLoader()

  // New filter states
  const [timeRange, setTimeRange] = useState('')
  const [consultantType, setConsultantType] = useState('')
  const [problemType, setProblemType] = useState('')
  const [userGender, setUserGender] = useState('')
  const [consultantGender, setConsultantGender] = useState('')
  const [timeZone, setTimeZone] = useState('')

  const [masterData, setMasterData] = useState([])
  const [consultantSkillOption, setConsultantSkillOption] = useState([])
  const [otherSkillOption, setOtherSkillOption] = useState([])
  const [expertiseOption, setExpertiseOption] = useState([])
  const [languageOption, setLanguageOption] = useState([])
  const [consultantSkillLoader, setConsultantSkillLoader] = useState(false)
  
  // Add totals state
  const [totals, setTotals] = useState({})
  
  // Add error state
  const [error, setError] = useState(null)

  // Tab state for chat/call separation
  const [activeTab, setActiveTab] = useState(0) // 0: Combined, 1: Chat, 2: Call

  // Column selection states
  const [columnSelectorOpen, setColumnSelectorOpen] = useState(false)
  const [visibleColumns, setVisibleColumns] = useState({
    // Date & Filters
    date: true,
    consultantType: true,
    problemType: true,
    consultantGender: true,
    userGender: true,
    
    // Numerical Data (Total Chat + Call)
    totalAttempts: true,
    totalFailed: true,
    totalCompleted: true,
    totalPaidCompleted: true,
    newUserRegistrations: true,


    
    // Chat Columns
    totalAttemptsChats: true,
    totalFailedChats: true,
    totalCompletedChats: true,
    totalFreeChats: true,
    totalPaidChats: true,
    totalFirstChats: true,
    totalRepeatChats: true,
    
    // Call Columns
    totalAttemptsCalls: true,
    totalFailedCalls: true,
    totalCompletedCalls: true,
    totalFreeCalls: true,
    totalPaidCalls: true,
    totalFirstCalls: true,
    totalRepeatCalls: true,
    
    // Time Spent (Chat + Call)
    totalTime: true,
    avgTime: true,
    
    // Chat (Time Spent)
    totalTimeChats: true,
    avgTimeChats: true,
    totalTimeFreeChat: true,
    avgTimeFreeChat: true,
    totalTimePaidChat: true,
    avgTimePaidChat: true,
    totalTimeFirstChat: true,
    avgTimeFirstChat: true,
    totalTimeRepeatChats: true,
    avgTimeRepeatChats: true,
  
    // Call (Time Spent)
    totalTimeCalls: true,
    avgTimeCalls: true,
    totalTimeFreeCalls: true,
    avgTimeFreeCalls: true,
    totalTimePaidCalls: true,
    avgTimePaidCalls: true,
    totalTimeFirstCalls: true,
    avgTimeFirstCalls: true,
    totalTimeRepeatCalls: true,
    avgTimeRepeatCalls: true,
    

 
    // Money Spent (Chat + Call)
    totalSpending: true,
    avgSpending: true,
    totalRecharge: true,
    uniqueRechargeUsers: true,
    
    // Chat (Money Spent)
    totalPaidChatSpending: true,
    avgPaidChatSpending: true,
    totalFirstPaidChatSpending: true,
    avgFirstPaidChatSpending: true,
    totalRepeatPaidChatSpending: true,
    avgRepeatPaidChatSpending: true,
    
    // Call (Money Spent)
    totalPaidCallsSpending: true,
    avgPaidCallsSpending: true,
    totalFirstPaidCallsSpending: true,
    avgFirstPaidCallsSpending: true,
    totalRepeatPaidCallsSpending: true,
    avgRepeatPaidCallsSpending: true
  })

  // Column definitions with categories
  const columnDefinitions = {
    date: { category: 'Date & Filters', label: 'Date' },
    consultantType: { category: 'Date & Filters', label: 'Consultant Type' },
    problemType: { category: 'Date & Filters', label: 'Problem Type' },
    consultantGender: { category: 'Date & Filters', label: 'Consultant Gender' },
    userGender: { category: 'Date & Filters', label: 'User Gender' },
    
    // Numerical Data (Total Chat + Call)
    totalAttempts: { category: 'Numerical Data (Total Chat + Call)', label: 'Total Attempt (Chat & Call)' },
    totalFailed: { category: 'Numerical Data (Total Chat + Call)', label: 'Total Failed (Chat & Call)' },
    totalCompleted: { category: 'Numerical Data (Total Chat + Call)', label: 'Total Completed (Chat & Call)' },
    totalPaidCompleted: { category: 'Numerical Data (Total Chat + Call)', label: 'Total Paid Completed (Chat & Call)' },
    newUserRegistrations: { category: 'Numerical Data (Total Chat + Call)', label: 'New User Registrations' },
    // Chat Columns
    totalAttemptsChats: { category: 'Chat Columns', label: 'Total Attempt (Chat)' },
    totalFailedChats: { category: 'Chat Columns', label: 'Total Failed (Chat)' },
    totalCompletedChats: { category: 'Chat Columns', label: 'Total Completed (Chat)' },
    totalFreeChats: { category: 'Chat Columns', label: 'Total Free Chat' },
    totalPaidChats: { category: 'Chat Columns', label: 'Total Paid Chat' },
    totalFirstChats: { category: 'Chat Columns', label: 'Total 1st Paid Chat' },
    totalRepeatChats: { category: 'Chat Columns', label: 'Total Repeat Paid Chat' },
    
    // Call Columns
    totalAttemptsCalls: { category: 'Call Columns', label: 'Total Attempt (Call)' },
    totalFailedCalls: { category: 'Call Columns', label: 'Total Failed (Call)' },
    totalCompletedCalls: { category: 'Call Columns', label: 'Total Completed (Call)' },
    totalFreeCalls: { category: 'Call Columns', label: 'Total Free Call' },
    totalPaidCalls: { category: 'Call Columns', label: 'Total Paid Call' },
    totalFirstCalls: { category: 'Call Columns', label: 'Total 1st Paid Call' },
    totalRepeatCalls: { category: 'Call Columns', label: 'Total Repeat Paid Call' },
    
    // Time Spent (Chat + Call)
    totalTime: { category: 'Time Spent (Chat + Call)', label: 'Total Time (Chat + Call)' },
    avgTime: { category: 'Time Spent (Chat + Call)', label: 'Avg. Time (Chat + Call)' },
    
    // Chat (Time Spent)
    totalTimeChats: { category: 'Chat (Time Spent)', label: 'Total Time (Chat)' },
    avgTimeChats: { category: 'Chat (Time Spent)', label: 'Avg. Time (Chat)' },
    totalTimeFreeChat: { category: 'Chat (Time Spent)', label: 'Total Time (Free Chat)' },
    avgTimeFreeChat: { category: 'Chat (Time Spent)', label: 'Avg. Time (Free Chat)' },
    totalTimePaidChat: { category: 'Chat (Time Spent)', label: 'Total Time (Paid Chat)' },
    avgTimePaidChat: { category: 'Chat (Time Spent)', label: 'Avg. Time (Paid Chat)' },
    totalTimeFirstChat: { category: 'Chat (Time Spent)', label: 'Total Time (1st Paid Chat)' },
    avgTimeFirstChat: { category: 'Chat (Time Spent)', label: 'Avg. Time (1st Paid Chat)' },
    totalTimeRepeatChats: { category: 'Chat (Time Spent)', label: 'Total Time (Repeat Paid Chat)' },
    avgTimeRepeatChats: { category: 'Chat (Time Spent)', label: 'Avg. Time (Repeat Paid Chat)' },
    
    // Call (Time Spent)
    totalTimeCalls: { category: 'Call (Time Spent)', label: 'Total Time (Call)' },
    avgTimeCalls: { category: 'Call (Time Spent)', label: 'Avg. Time (Call)' },
    totalTimeFreeCalls: { category: 'Call (Time Spent)', label: 'Total Time (Free Call)' },
    avgTimeFreeCalls: { category: 'Call (Time Spent)', label: 'Avg. Time (Free Call)' },
    totalTimePaidCalls: { category: 'Call (Time Spent)', label: 'Total Time (Paid Call)' },
    avgTimePaidCalls: { category: 'Call (Time Spent)', label: 'Avg. Time (Paid Call)' },
    totalTimeFirstCalls: { category: 'Call (Time Spent)', label: 'Total Time (1st Paid Call)' },
    avgTimeFirstCalls: { category: 'Call (Time Spent)', label: 'Avg. Time (1st Paid Call)' },
    totalTimeRepeatCalls: { category: 'Call (Time Spent)', label: 'Total Time (Repeat Paid Call)' },
    avgTimeRepeatCalls: { category: 'Call (Time Spent)', label: 'Avg. Time (Repeat Paid Call)' },
    
    // Money Spent (Chat + Call)
    totalSpending: { category: 'Money Spent (Chat + Call)', label: 'Total Spent (Chat + Call)' },
    avgSpending: { category: 'Money Spent (Chat + Call)', label: 'Avg. Spent (Chat + Call)' },
    totalRecharge: { category: 'Money Spent (Chat + Call)', label: 'No. of Recharge' },
    uniqueRechargeUsers: { category: 'Money Spent (Chat + Call)', label: 'Unique Recharge Users' },
    
    // Chat (Money Spent)
    totalPaidChatSpending: { category: 'Chat (Money Spent)', label: 'Total Spent (Paid Chat)' },
    avgPaidChatSpending: { category: 'Chat (Money Spent)', label: 'Avg. Spent (Paid Chat)' },
    totalFirstPaidChatSpending: { category: 'Chat (Money Spent)', label: 'Total Spent (1st Paid Chat)' },
    avgFirstPaidChatSpending: { category: 'Chat (Money Spent)', label: 'Avg. Spent (1st Paid Chat)' },
    totalRepeatPaidChatSpending: { category: 'Chat (Money Spent)', label: 'Total Spent (Repeat Paid Chat)' },
    avgRepeatPaidChatSpending: { category: 'Chat (Money Spent)', label: 'Avg. Spent (Repeat Paid Chat)' },
    
    // Call (Money Spent)
    totalPaidCallsSpending: { category: 'Call (Money Spent)', label: 'Total Spent (Paid Call)' },
    avgPaidCallsSpending: { category: 'Call (Money Spent)', label: 'Avg. Spent (Paid Call)' },
    totalFirstPaidCallsSpending: { category: 'Call (Money Spent)', label: 'Total Spent (1st Paid Call)' },
    avgFirstPaidCallsSpending: { category: 'Call (Money Spent)', label: 'Avg. Spent (1st Paid Call)' },
    totalRepeatPaidCallsSpending: { category: 'Call (Money Spent)', label: 'Total Spent (Repeat Paid Call)' },
    avgRepeatPaidCallsSpending: { category: 'Call (Money Spent)', label: 'Avg. Spent (Repeat Paid Call)' }
  }

  // Column information for tooltips
  const columnInfo = {
    date: 'The date when the data was recorded',
    consultantType: 'Type of consultant (e.g., Astrologer, Numerologist, etc.)',
    problemType: 'Category of problem the user consulted for',
    consultantGender: 'Gender of the consultant',
    userGender: 'Gender of the user',
    // Numerical Data (Total Chat + Call)
    totalAttempts: 'Total number of Chats and Calls initiated, regardless of type (free/paid) or outcome (completed, failed, cancelled).',
    totalCompleted: 'Total number of Chats and Calls that were completed, including both free and paid.',
    totalFailed : 'Total number of Chats and Calls that were not completed, including all failure reasons (user cancelled, consultant cancelled, technical issues, etc.).',
    totalPaidCompleted: 'Total number of Chats and Calls that were completed, including both free and paid.',
    newUserRegistrations: 'Total number of new user registrations.',  
    // Chat Columns
    totalAttemptsChats: 'Total number of Chats initiated, regardless of type (free/paid) or outcome (completed, failed, or cancelled).',
    totalFailedChats: 'Total number of Chats that were not completed, covering all failure reasons (e.g., user cancelled, consultant cancelled, technical issues).',
    totalCompletedChats: 'Total number of successfully completed Chats, including both free and paid.',
    totalFreeChats: 'Total number of successfully completed free chat consultations.',
    totalPaidChats: 'Total number of successfully completed paid chat consultations (all types included).',
    totalFirstChats: 'Total number of successfully completed first-time paid chat consultations (user\'s first paid consultation).',
    totalRepeatChats: 'Total number of successfully completed repeat paid chat consultations (user has taken at least one paid consultation before).',
    
    // Call Columns
    totalAttemptsCalls: 'Total number of Calls initiated, regardless of type (free/paid) or outcome (completed, failed, or cancelled).',
    totalFailedCalls: 'Total number of Calls that were not completed, covering all failure reasons (e.g., user cancelled, consultant cancelled, technical issues).',
    totalCompletedCalls: 'Total number of successfully completed Calls, including both free and paid.',
    totalFreeCalls: 'Total number of successfully completed free Call consultations.',
    totalPaidCalls: 'Total number of successfully completed paid Call consultations (all types included).',
    totalFirstCalls: 'Total number of successfully completed first-time paid Call consultations (user\'s first paid consultation).',
    totalRepeatCalls: 'Total number of successfully completed repeat paid Call consultations (user has taken at least one paid consultation before).',
    
    // Time Spent (Chat + Call)
    totalTime: 'Combined total time spent on all Chats and Calls, including both free and paid.',
    avgTime: 'Average time spent per Chat or Call, including both free and paid.',
    
    // Chat (Time Spent)
    totalTimeChats: 'Total time spent on all Chats, including both free and paid.',
    avgTimeChats: 'Average time spent per Chat, including both free and paid.',
    totalTimeFreeChat: 'Total time spent on free chats, i.e., total minutes of all free chat consultations.',
    avgTimeFreeChat: 'Average duration of a free chat = Total Time (Free Chat) ÷ Total number of free chats.',
    totalTimePaidChat: 'Total time spent on paid chats, i.e., total minutes of all paid chat consultations.',
    avgTimePaidChat: 'Average duration of a paid chat = Total Time (Paid Chat) ÷ Total number of paid chats.',
    totalTimeFirstChat: 'Total time spent on first-time paid chats, i.e., total minutes from users taking their first paid consultation.',
    avgTimeFirstChat: 'Average duration of a first-time paid chat = Total Time (1st Paid Chat) ÷ Total number of first-time paid chats.',
    totalTimeRepeatChats: 'Total time spent on repeat paid chats, i.e., total minutes from users returning for another paid consultation.',
    avgTimeRepeatChats: 'Average duration of a repeat paid chat = Total Time (Repeat Paid Chat) ÷ Total number of repeat paid chats.',
    
    // Call (Time Spent)
    totalTimeCalls: 'Total time spent on all Calls, including both free and paid.',
    avgTimeCalls: 'Average time spent per Call, including both free and paid.',
    totalTimeFreeCalls: 'Total time spent on free calls, i.e., total minutes of all free call consultations.',
    avgTimeFreeCalls: 'Average duration of a free call = Total Time (Free Call) ÷ Total number of free calls.',
    totalTimePaidCalls: 'Total time spent on paid calls, i.e., total minutes of all paid call consultations.',
    avgTimePaidCalls: 'Average duration of a paid call = Total Time (Paid Call) ÷ Total number of paid calls.',
    totalTimeFirstCalls: 'Total time spent on first-time paid calls, i.e., total minutes from users taking their first paid call consultation.',
    avgTimeFirstCalls: 'Average duration of a first-time paid call = Total Time (1st Paid Call) ÷ Total number of first-time paid calls.',
    totalTimeRepeatCalls: 'Total time spent on repeat paid calls, i.e., total minutes from users returning for another paid call consultation.',
    avgTimeRepeatCalls: 'Average duration of repeat paid calls = Total Time (Repeat Paid Call) ÷ Total number of repeat paid calls.',
    
    // Money Spent (Chat + Call)
    totalSpending: 'Combined total money spent for all paid Chats and Calls.',
    avgSpending: 'Average money spent per paid Chat or Call = Total Spent (Chat + Call) ÷ Total number of paid consultations (Chat + Call).',
    totalRecharge: 'Total number of wallet recharges completed by users.',
    uniqueRechargeUsers: 'Number of unique users who completed wallet recharges.',
    
    // Chat (Money Spent)
    totalPaidChatSpending: 'Total money spent on paid chats, i.e., total money spent on all paid chat consultations.',
    avgPaidChatSpending: 'Average money spent on a paid chat = Total Spent (Paid Chat) ÷ Total number of paid chats.',
    totalFirstPaidChatSpending: 'Total money spent on first-time paid chats, i.e., total money spent from users taking their first paid chat consultation.',
    avgFirstPaidChatSpending: 'Average money spent on a first-time paid chat = Total Spent (1st Paid Chat) ÷ Total number of first-time paid chats.',
    totalRepeatPaidChatSpending: 'Total money spent on repeat paid chats, i.e., total money spent from users returning for another paid chat consultation.',
    avgRepeatPaidChatSpending: 'Average money spent on a repeat paid chat = Total Money (Repeat Paid Chat) ÷ Total number of repeat paid chats.',
    
    // Call (Money Spent)
    totalPaidCallsSpending: 'Total money spent on paid calls, i.e., total money spent on all paid call consultations.',
    avgPaidCallsSpending: 'Average money spent on a paid call = Total Spent (Paid Call) ÷ Total number of paid calls.',
    totalFirstPaidCallsSpending: 'Total money spent on first-time paid calls, i.e., total money spent from users taking their first paid call consultation.',
    avgFirstPaidCallsSpending: 'Average money spent on a first-time paid call = Total Spent (1st Paid Call) ÷ Total number of first-time paid calls.',
    totalRepeatPaidCallsSpending: 'Total money spent on repeat paid calls, i.e., total money spent from users returning for another paid call consultation.',
    avgRepeatPaidCallsSpending: 'Average money spent on a repeat paid call = Total Money (Repeat Paid Call) ÷ Total number of repeat paid calls.'
  }

  // Helper function to get column info
  const getColumnInfo = (columnKey) => {
    return columnInfo[columnKey] || 'No information available for this column'
  }

  // Get columns based on active tab
  const getColumnsForActiveTab = () => {
    const tabMappings = {
      0: 'all',    // Combined view
      1: 'chat',   // Chat only
      2: 'call'    // Call only
    }
    
    const currentTab = tabMappings[activeTab]
    return Object.keys(columnDefinitions).filter(columnKey => {
      const category = columnDefinitions[columnKey].category
      if (currentTab === 'all') return true
      if (currentTab === 'chat') {
        return category.includes('Chat') || 
               category === 'Date & Filters' || 
               category === 'Total Time Insights' || 
               category === 'Total Spending Insights'
      }
      if (currentTab === 'call') {
        return category.includes('Call') || 
               category === 'Date & Filters' || 
               category === 'Total Time Insights' || 
               category === 'Total Spending Insights'
      }
      return false
    })
  }

  // Get visible columns for current tab
  const getVisibleColumnsForCurrentTab = () => {
    const tabColumns = getColumnsForActiveTab()
    const visible = {}
    console.log(tabColumns)
    tabColumns.forEach(columnKey => {
      visible[columnKey] = visibleColumns[columnKey]
    })
    
    return visible
  }

  // Get all columns grouped by category (both visible and hidden)
  const getAllColumnsByCategory = () => {
    const categories = {}
    const tabColumns = getColumnsForActiveTab()
    
    tabColumns.forEach(columnKey => {
      const category = columnDefinitions[columnKey].category
      if (!categories[category]) {
        categories[category] = []
      }
      categories[category].push({
        key: columnKey,
        ...columnDefinitions[columnKey]
      })
    })
    return categories
  }

  // Handle column selection
  const handleColumnToggle = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }))
  }

  // Handle select all columns
  const handleSelectAllColumns = () => {
    const allColumns = {}
    const tabColumns = getColumnsForActiveTab()
    tabColumns.forEach(key => {
      allColumns[key] = true
    })
    setVisibleColumns(prev => ({ ...prev, ...allColumns }))
  }

  // Handle deselect all columns
  const handleDeselectAllColumns = () => {
    const noColumns = {}
    const tabColumns = getColumnsForActiveTab()
    tabColumns.forEach(key => {
      noColumns[key] = false
    })
    setVisibleColumns(prev => ({ ...prev, ...noColumns }))
  }

  // Handle select all in category
  const handleSelectAllInCategory = (category) => {
    setVisibleColumns(prev => {
      const updated = { ...prev }
      const tabColumns = getColumnsForActiveTab()
      tabColumns.forEach(key => {
        if (columnDefinitions[key].category === category) {
          updated[key] = true
        }
      })
      return updated
    })
  }

  // Handle deselect all in category
  const handleDeselectAllInCategory = (category) => {
    setVisibleColumns(prev => {
      const updated = { ...prev }
      const tabColumns = getColumnsForActiveTab()
      tabColumns.forEach(key => {
        if (columnDefinitions[key].category === category) {
          updated[key] = false
        }
      })
      return updated
    })
  }

  // Get column count
  const getVisibleColumnCount = () => {
    const visibleColumnsForTab = getVisibleColumnsForCurrentTab()
    return Object.values(visibleColumnsForTab).filter(Boolean).length
  }

  // Helper function to determine if a column is chat-related
  const isChatColumn = (columnKey) => {
    const category = columnDefinitions[columnKey]?.category
    return category && (
      category.includes('Chat') || 
      category === 'Chat Columns' || 
      category === 'Chat (Time Spent)' || 
      category === 'Chat (Money Spent)'
    )
  }

  // Helper function to determine if a column is call-related
  const isCallColumn = (columnKey) => {
    const category = columnDefinitions[columnKey]?.category
    return category && (
      category.includes('Call') || 
      category === 'Call Columns' || 
      category === 'Call (Time Spent)' || 
      category === 'Call (Money Spent)'
    )
  }

  // Helper function to determine if a column is common (not chat or call specific)
  const isCommonColumn = (columnKey) => {
    return !isChatColumn(columnKey) && !isCallColumn(columnKey)
  }

  // Helper function to get cell border style based on column type
  const getCellBorderStyle = (columnKey) => {
    if (isChatColumn(columnKey)) {
      return {
        borderLeft: '2px solid #2e7d32', // Green border for chat columns
        borderRight: '2px solid #2e7d32',
        backgroundColor: '#f8f9fa' // Light background for rows
      }
    } else if (isCallColumn(columnKey)) {
      return {
        borderLeft: '2px solid #f57c00', // Orange border for call columns
        borderRight: '2px solid #f57c00',
        backgroundColor: '#f8f9fa' // Light background for rows
      }
    } else {
      return {
        borderLeft: '1px solid #e0e0e0',
        borderRight: '1px solid #e0e0e0',
        backgroundColor: '#f8f9fa' // Light background for rows
      }
    }
  }

  // Helper function to get header border style based on column type
  const getHeaderBorderStyle = (columnKey) => {
    if (isChatColumn(columnKey)) {
      return {
        borderLeft: '2px solid #2e7d32', // Green border for chat columns
        borderRight: '2px solid #2e7d32',
        backgroundColor: '#e8f5e8' // Light green background
      }
    } else if (isCallColumn(columnKey)) {
      return {
        borderLeft: '2px solid #f57c00', // Orange border for call columns
        borderRight: '2px solid #f57c00',
        backgroundColor: '#fff3e0' // Light orange background
      }
    } else {
      return {
        borderLeft: '1px solid #e0e0e0',
        borderRight: '1px solid #e0e0e0',
        backgroundColor: '#f8f9fa' // Light gray background for common columns
      }
    }
  }

  // Helper function to get alternating row style
  const getRowStyle = (index) => {
    return {
      backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa' // Alternating row colors
    }
  }

  // Helper functions for colspan calculations
  const getVisibleDateColumnsCount = () => {
    const visibleColumnsForTab = getVisibleColumnsForCurrentTab()
    return Object.keys(visibleColumnsForTab).filter(key => 
      columnDefinitions[key]?.category === 'Date & Filters' && visibleColumnsForTab[key]
    ).length
  }

  const getVisibleNumericalDataColumnsCount = () => {
    const visibleColumnsForTab = getVisibleColumnsForCurrentTab()
    return Object.keys(visibleColumnsForTab).filter(key => 
      columnDefinitions[key]?.category === 'Numerical Data (Total Chat + Call)' && visibleColumnsForTab[key]
    ).length
  }

  const getVisibleChatColumnsCount = () => {
    const visibleColumnsForTab = getVisibleColumnsForCurrentTab()
    return Object.keys(visibleColumnsForTab).filter(key => 
      columnDefinitions[key]?.category === 'Chat Columns' && visibleColumnsForTab[key]
    ).length
  }

  const getVisibleCallColumnsCount = () => {
    const visibleColumnsForTab = getVisibleColumnsForCurrentTab()
    return Object.keys(visibleColumnsForTab).filter(key => 
      columnDefinitions[key]?.category === 'Call Columns' && visibleColumnsForTab[key]
    ).length
  }

  const getVisibleTimeColumnsCount = () => {
    const visibleColumnsForTab = getVisibleColumnsForCurrentTab()
    return Object.keys(visibleColumnsForTab).filter(key => 
      (columnDefinitions[key]?.category === 'Time Spent (Chat + Call)' || 
       columnDefinitions[key]?.category === 'Chat (Time Spent)' || 
       columnDefinitions[key]?.category === 'Call (Time Spent)') && visibleColumnsForTab[key]
    ).length
  }

  const getVisibleMoneyColumnsCount = () => {
    const visibleColumnsForTab = getVisibleColumnsForCurrentTab()
    return Object.keys(visibleColumnsForTab).filter(key => 
      (columnDefinitions[key]?.category === 'Money Spent (Chat + Call)' || 
       columnDefinitions[key]?.category === 'Chat (Money Spent)' || 
       columnDefinitions[key]?.category === 'Call (Money Spent)') && visibleColumnsForTab[key]
    ).length
  }

  // Render cell value based on column type
  const renderCellValue = (item, columnKey) => {
    const columnDef = columnDefinitions[columnKey]
    const value = item[columnKey]
    
    switch (columnDef.type) {
      case 'number':
        return (value || 0)
      case 'percentage':
        return `${(value || 0)}%`
      case 'time':
        return formatTime(value || 0)
      case 'currency':
        return formatCurrency(value || 0)
      case 'date':
        return moment(value).format('DD MMM YYYY')
      case 'text':
      default:
        return value || 'N/A'
    }
  }

  // fetch consultant skill
  const fetchMasterData = async () => {
    try {
      const { data } = await fatchCategoryApi();
      if (data.code === 200) {
        const updatedConsultantSkill =
          data?.result?.skillList &&
          data?.result?.skillList.length > 0 &&
          data?.result?.skillList.map(({ _id, title }) => ({
            value: title,
            label: title,
          }));

        setConsultantSkillOption(updatedConsultantSkill);
        setOtherSkillOption(updatedConsultantSkill);
 

        const expertise =
          data?.result?.categoryList &&
          data?.result?.categoryList.length > 0 &&
          data.result.categoryList.map(({ _id, title }) => ({
            value: title,
            label: title,
          }));
        setExpertiseOption(expertise);

        const language =
          data?.result?.languageList &&
          data?.result?.languageList.length > 0 &&
          data.result.languageList.map(({ _id, title }) => ({
            value: title,
            label: title,
          }));

        console.log(language, "language");
        setLanguageOption(language);
      } else {
        setConsultantSkillOption([]);
        setOtherSkillOption([]);
        setExpertiseOption([]);
        setLanguageOption([]);

        toastMessage(data.message, "error", "categoryList");
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setConsultantSkillLoader(false);
    }
  };

  useEffect(() => {
        fetchMasterData();
  }, []);
  // Fetch user insights data
  const fetchUserInsights = async (page = 1, search = '', start = '', end = '', filters = {}) => {
    try {
      showLoader()
      setError(null)
      const sendData = {
        page,
        pageSize: pageSize,
        search,
        ...(start && { startDate: start }),
        ...(end && { endDate: end }),
        ...(filters.timeRange && { timeRange: filters.timeRange }),
        ...(filters.consultantType && { consultantType: filters.consultantType }),
        ...(filters.problemType && { problemType: filters.problemType }),
        ...(filters.userGender && { userGender: filters.userGender }),
        ...(filters.consultantGender && { consultantGender: filters.consultantGender }),
        ...(filters.timeZone && { timeZone: filters.timeZone })
      }
      console.log("sendData", sendData)
      const { data } = await fetchUserInsightsApi(sendData)
      if (data?.code === 200) {
        setUserList(data?.result?.list || [])
        setTotal(data?.result?.pagination?.totalDocuments || 0)
        setTotals(data?.result?.totals || {}) // Update totals state
        setError(null)
      } else {
        setUserList([])
        setTotal(0)
        setError(data?.message || 'Failed to fetch data')
        toastMessage(data?.message, "error", "UserInsight")
      }
    } catch (error) {
      console.error("Error fetching user insights:", error)
      setError('Network error. Please try again.')
      toastMessage("Failed to fetch user insights data", "error", "UserInsight")
      setUserList([])
      setTotal(0)
    } finally {
      hideLoader()
    }
  }

  useEffect(() => {
    const filters = {
      timeRange,
      consultantType,
      problemType,
      userGender,
      consultantGender,
      timeZone
    }
    console.log('Frontend filters being sent:', filters);
    fetchUserInsights(activePage, searchQuery, startDate, endDate, filters)
  }, [activePage, pageSize, searchQuery, startDate, endDate, timeRange, consultantType, problemType, userGender, consultantGender, timeZone])

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setActivePage(pageNumber)
  }

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize)
    setActivePage(1) // Reset to first page when changing page size
  }

  // Handle search
  const handleSearch = (event) => {
    setSearchQuery(event.target.value)
    setActivePage(1) // Reset to first page when searching
  }

  // Handle date change
  const handleDateChange = (type, value) => {
    if (type === 'start') {
      setStartDate(value)
    } else {
      setEndDate(value)
    }
    setActivePage(1) // Reset to first page when changing dates
  }

  // Handle time range change
  const handleTimeRangeChange = (value) => {
    console.log('Time range changed to:', value);
    setTimeRange(value)
    setActivePage(1)
  }

  // Handle consultant type change
  const handleConsultantTypeChange = (value) => {
    setConsultantType(value)
    setActivePage(1)
  }

  // Handle problem type change
  const handleProblemTypeChange = (value) => {
    setProblemType(value)
    setActivePage(1)
  }

  // Handle user gender change
  const handleUserGenderChange = (value) => {
    setUserGender(value)
    setActivePage(1)
  }

  // Handle consultant gender change
  const handleConsultantGenderChange = (value) => {
    setConsultantGender(value)
    setActivePage(1)
  }

  // Handle time zone change
  const handleTimeZoneChange = (value) => {
    setTimeZone(value)
    setActivePage(1)
  }

  // Clear filters
  const clearFilters = () => {
    setSearchQuery('')
    setStartDate('')
    setEndDate('')
    setTimeRange('')
    setConsultantType('')
    setProblemType('')
    setUserGender('')
    setConsultantGender('')
    setTimeZone('')
    setActivePage(1)
  }

  // Filter options data
  const timeRangeOptions = [
    { value: 'today', label: 'Today' },
    { value: 'last7days', label: 'Last 7 Days' },
    { value: 'last30days', label: 'Last 30 Days' },
    { value: 'last60days', label: 'Last 60 Days' },
    { value: 'last90days', label: 'Last 90 Days' },
    { value: 'custom', label: 'Custom Range' }
  ]

  const consultantTypeOptions = [
    { value: 'astrology', label: 'Astrology' },
    { value: 'numerology', label: 'Numerology' },
    { value: 'palmistry', label: 'Palmistry' },
    { value: 'tarot', label: 'Tarot' },
    { value: 'vastu', label: 'Vastu' },
    { value: 'other', label: 'Other' }
  ]

  const problemTypeOptions = [
    { value: 'health', label: 'Health' },
    { value: 'finance', label: 'Finance' },
    { value: 'marriage', label: 'Marriage' },
    { value: 'relationship', label: 'Relationship' },
    { value: 'depression', label: 'Depression' },
    { value: 'other', label: 'Other' }
  ]

  const genderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'binary', label: 'Transgender' }
  ]

  const consultantGenderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'binary', label: 'Transgender' }

  ]

  const timeZoneOptions = [
    { value: '12pm-2pm', label: '12 PM - 2 PM' },
    { value: '2pm-4pm', label: '2 PM - 4 PM' },
    { value: '4pm-6pm', label: '4 PM - 6 PM' },
    { value: '6pm-8pm', label: '6 PM - 8 PM' },
    { value: '8pm-10pm', label: '8 PM - 10 PM' },
    { value: '10pm-12am', label: '10 PM - 12 AM' },
    { value: '12am-2am', label: '12 AM - 2 AM' },
    { value: '2am-4am', label: '2 AM - 4 AM' },
    { value: '4am-6am', label: '4 AM - 6 AM' },
    { value: '6am-8am', label: '6 AM - 8 AM' },
    { value: '8am-10am', label: '8 AM - 10 AM' },
    { value: '10am-12pm', label: '10 AM - 12 PM' }
  ]

  // Format number for display
  const formatNumber = (value) => (value ? Number(value).toFixed(2) : "0")
  
  // Format time in minutes and seconds
  const formatTime = (seconds) => {
    if (!seconds) return "0 min 0 sec"
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = Math.floor(seconds % 60)
    return `${minutes} min ${remainingSeconds} sec`
  }

  // Format currency
  const formatCurrency = (amount) => {
    return amount ? `₹${formatNumber(amount)}` : "₹0.00"
  }

  // Calculate serial number
  const findSerialNumber = (index, currentPage, pageSize) => {
    return (currentPage - 1) * pageSize + index + 1
  }

  // Helper function to format filter display values
  const formatFilterValue = (value, type) => {
    if (!value) {
      switch (type) {
        case 'consultantType':
          return 'All Types';
        case 'problemType':
          return 'All Problems';
        case 'gender':
          return 'All Genders';
        default:
          return 'All';
      }
    }
    return value;
  }



  return (
    <div className="WrapperArea">
      <div className="WrapperBox">
        <div className="TitleBox">
          <div className="user_detail_heading">
            <Link className="back_icon" to={-2}>
              <IconButton>
                <ArrowBackIosNewIcon className="back_button" />
              </IconButton>
            </Link>
            <h4 className="Title">User Insight Details</h4>
          </div>
        </div>
        {loader}
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <Typography variant="h4" className="mt-4 mb-4">
                User Insights Dashboard - Date Wise
              </Typography>

              {/* Search and Filter Bar */}
              <Box className="mb-4">
                <Typography variant="h6" className="mb-3">
                  Filters
                </Typography>
                 {/* Second Row - Consultant and Problem Type */}
                 <Grid container spacing={2} alignItems="center" className="mb-3">
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Consultant Type</InputLabel>
                      <Select
                        value={consultantType}
                        onChange={(e) => handleConsultantTypeChange(e.target.value)}
                        label="Consultant Type"
                      >
                        <MenuItem value="">
                          <em>All Consultant Types</em>
                        </MenuItem>
                        {consultantSkillOption.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Problem Type</InputLabel>
                      <Select
                        value={problemType}
                        onChange={(e) => handleProblemTypeChange(e.target.value)}
                        label="Problem Type"
                      >
                        <MenuItem value="">
                          <em>All Problem Types</em>
                        </MenuItem>
                        {expertiseOption.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>User Gender</InputLabel>
                      <Select
                        value={userGender}
                        onChange={(e) => handleUserGenderChange(e.target.value)}
                        label="User Gender"
                      >
                        <MenuItem value="">
                          <em>All Genders</em>
                        </MenuItem>
                        {genderOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Consultant Gender</InputLabel>
                      <Select
                        value={consultantGender}
                        onChange={(e) => handleConsultantGenderChange(e.target.value)}
                        label="Consultant Gender"
                      >
                        <MenuItem value="">
                          <em>All Consultant Genders</em>
                        </MenuItem>
                        {consultantGenderOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                
                {/* First Row - Search and Time Range */}
                <Grid container spacing={2} alignItems="center" className="mb-3">
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      placeholder="Search by date, attempts, or spending..."
                      value={searchQuery}
                      onChange={handleSearch}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Time Range</InputLabel>
                      <Select
                        value={timeRange}
                        onChange={(e) => handleTimeRangeChange(e.target.value)}
                        label="Time Range"
                      >
                        <MenuItem value="">
                          <em>All Time Ranges</em>
                        </MenuItem>
                        {timeRangeOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  {
                    timeRange === 'custom' && (
                        <>
                        <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          type="date"
                          variant="outlined"
                          label="Start Date"
                          value={startDate}
                          onChange={(e) => handleDateChange('start', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                         <Grid item xs={12} md={3}>
                         <TextField
                           fullWidth
                           type="date"
                           variant="outlined"
                           label="End Date"
                           value={endDate}
                           onChange={(e) => handleDateChange('end', e.target.value)}
                           InputLabelProps={{ shrink: true }}
                         />
                       </Grid>
                       </>

                    )
                  }
                 
               
                </Grid>

               

                {/* Third Row - Time Zone and Clear Filters */}
                <Grid container spacing={2} alignItems="center">
                  {/* <Grid item xs={12} md={4}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Time Zone</InputLabel>
                      <Select
                        value={timeZone}
                        onChange={(e) => handleTimeZoneChange(e.target.value)}
                        label="Time Zone"
                      >
                        <MenuItem value="">
                          <em>All Time Zones</em>
                        </MenuItem>
                        {timeZoneOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid> */}
                  <Grid item xs={12} md={2}>
                    <Button
                      variant="outlined"
                      onClick={clearFilters}
                      fullWidth
                      style={{ height: '56px' }}
                    >
                      Clear Filters
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              {/* Error Display */}
              {error && (
                <Box className="mb-4">
                  <Card sx={{ backgroundColor: '#ffebee' }}>
                    <CardContent>
                      <Typography color="error" variant="h6">
                        Error: {error}
                      </Typography>
                      <Button 
                        variant="outlined" 
                        color="error" 
                        onClick={() => fetchUserInsights(activePage, searchQuery, startDate, endDate, {
                          timeRange,
                          consultantType,
                          problemType,
                          userGender,
                          consultantGender,
                          timeZone
                        })}
                        className="mt-2"
                      >
                        Retry
                      </Button>
                    </CardContent>
                  </Card>
                </Box>
              )}

              {/* Tab Navigation for Chat/Call Separation */}
              <Box className="mb-3">
                <Tabs 
                  value={activeTab} 
                  onChange={(e, newValue) => setActiveTab(newValue)}
                  variant="fullWidth"
                  sx={{ 
                    borderBottom: 1, 
                    borderColor: 'divider',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px 8px 0 0',
                    '& .MuiTab-root': {
                      minHeight: '48px',
                      textTransform: 'none',
                      fontWeight: 500,
                      '&.Mui-selected': {
                        backgroundColor: '#ffffff',
                        borderRadius: '8px 8px 0 0',
                        boxShadow: '0 -2px 4px rgba(0,0,0,0.1)'
                      },
                      '&:hover': {
                        backgroundColor: '#e9ecef',
                        borderRadius: '8px 8px 0 0'
                      }
                    },
                    '& .MuiTabs-indicator': {
                      backgroundColor: '#1976d2',
                      height: '3px'
                    }
                  }}
                >
                  <Tab 
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" sx={{ color: activeTab === 0 ? '#1976d2' : '#1976d2' }}>Combined View</Typography>
                        <Chip 
                          label="All" 
                          size="small" 
                          sx={{ 
                            backgroundColor: '#e3f2fd',
                            color: '#1976d2',
                            fontWeight: 500
                          }} 
                        />
                      </Box>
                    } 
                    icon={<ViewColumnIcon sx={{ color: activeTab === 0 ? '#1976d2' : '#1976d2' }} />}
                    iconPosition="start"
                  />
                  <Tab 
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" sx={{ color: activeTab === 1 ? '#2e7d32' : '#2e7d32' }}>Chat Analytics</Typography>
                        <Chip 
                          label="Chat" 
                          size="small" 
                          sx={{ 
                            backgroundColor: '#e8f5e8',
                            color: '#2e7d32',
                            fontWeight: 500
                          }} 
                        />
                      </Box>
                    } 
                    icon={<ChatIcon sx={{ color: activeTab === 1 ? '#2e7d32' : '#2e7d32' }} />}
                    iconPosition="start"
                  />
                  <Tab 
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" sx={{ color: activeTab === 2 ? '#f57c00' : '#f57c00' }}>Call Analytics</Typography>
                        <Chip 
                          label="Call" 
                          size="small" 
                          sx={{ 
                            backgroundColor: '#fff3e0',
                            color: '#f57c00',
                            fontWeight: 500
                          }} 
                        />
                      </Box>
                    } 
                    icon={<CallIcon sx={{ color: activeTab === 2 ? '#f57c00' : '#f57c00' }} />}
                    iconPosition="start"
                  />
                </Tabs>
              </Box>

              {/* Column Selector and Table Header */}
              <Box className="mb-3 d-flex justify-content-between align-items-center">
                <Typography variant="h5">
                  {activeTab === 0 && "User Insights Dashboard - Combined View"}
                  {activeTab === 1 && "User Insights Dashboard - Chat Analytics"}
                  {activeTab === 2 && "User Insights Dashboard - Call Analytics"}
                </Typography>
                <Box className="d-flex align-items-center gap-2">
                  <Chip 
                    label={`${getVisibleColumnCount()} columns selected`}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                  <Button
                    variant="outlined"
                    startIcon={<ViewColumnIcon />}
                    onClick={() => setColumnSelectorOpen(true)}
                  >
                    Customize Columns
                  </Button>
                </Box>
              </Box>

              {/* User Insights Table */}
              <Paper elevation={3} className="p-4">
              
                <div className="content-table" style={{ 
                  maxHeight: '70vh', 
                  overflowY: 'auto', 
                  overflowX: 'auto',
                  position: 'relative',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px'
                }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ 
                      position: 'sticky', 
                      top: 0, 
                      zIndex: 10,
                      backgroundColor: 'white',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      backdropFilter: 'blur(8px)'
                    }}>
                      <tr>
                        <th rowSpan="2" style={{ 
                          border: '1px solid #e0e0e0', 
                          backgroundColor: '#f8f9fa',
                          position: 'sticky',
                          left: 0,
                          zIndex: 11,
                          boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                        }}>S. No.</th>
                        
                        {/* Date & Filters Group */}
                        {getVisibleDateColumnsCount() > 0 && (
                          <th colSpan={getVisibleDateColumnsCount()} style={{ 
                            textAlign: 'center', 
                            backgroundColor: '#f8f9fa',
                            border: '2px solid #e0e0e0'
                          }}>
                            Date & Filters
                          </th>
                        )}
                        
                        {/* Numerical Data (Total Chat + Call) Group */}
                        {getVisibleNumericalDataColumnsCount() > 0 && (
                          <th colSpan={getVisibleNumericalDataColumnsCount()} style={{ textAlign: 'center', backgroundColor: '#e3f2fd' }}>
                            Numerical Data (Total Chat + Call)
                          </th>
                        )}
                        
                        {/* Chat Columns Group */}
                        {getVisibleChatColumnsCount() > 0 && (
                          <th colSpan={getVisibleChatColumnsCount()} style={{ textAlign: 'center', backgroundColor: '#e3f2fd' }}>
                            Chat Columns
                          </th>
                        )}
                        
                        {/* Call Columns Group */}
                        {getVisibleCallColumnsCount() > 0 && (
                          <th colSpan={getVisibleCallColumnsCount()} style={{ textAlign: 'center', backgroundColor: '#e3f2fd' }}>
                            Call Columns
                          </th>
                        )}
                        
                        {/* Time Insights Group */}
                        {getVisibleTimeColumnsCount() > 0 && (
                          <th colSpan={getVisibleTimeColumnsCount()} style={{ 
                            textAlign: 'center', 
                            backgroundColor: '#f3e5f5',
                            border: '2px solid #e0e0e0'
                          }}>
                            Time Insights
                          </th>
                        )}
                        
                        {/* Money Insights Group */}
                        {getVisibleMoneyColumnsCount() > 0 && (
                          <th colSpan={getVisibleMoneyColumnsCount()} style={{ 
                            textAlign: 'center', 
                            backgroundColor: '#ECEDB0',
                            border: '2px solid #e0e0e0'
                          }}>
                            Money Insights
                          </th>
                        )}
                      </tr>
                      <tr>
                        {/* Date Information Sub-columns */}
                        {getVisibleColumnsForCurrentTab().date && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#e0e0e0', color: '#333' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Date
                              <Tooltip title={getColumnInfo('date')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#666', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().consultantType && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#e0e0e0', color: '#333' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Consultant Type
                              <Tooltip title={getColumnInfo('consultantType')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#666', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().problemType && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#e0e0e0', color: '#333' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Problem Type
                              <Tooltip title={getColumnInfo('problemType')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#666', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().consultantGender && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#e0e0e0', color: '#333' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Consultant Gender
                              <Tooltip title={getColumnInfo('consultantGender')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#666', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().userGender && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#e0e0e0', color: '#333' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              User Gender
                              <Tooltip title={getColumnInfo('userGender')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#666', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                   
                        {/* Numerical Data (Total Chat + Call) Sub-columns */}
                        {getVisibleColumnsForCurrentTab().totalAttempts && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#e3f2fd', color: '#1976d2' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total Attempt (Chat & Call)
                              <Tooltip title={getColumnInfo('totalAttempts')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#1976d2', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().totalFailed && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#e3f2fd', color: '#1976d2' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total Failed (Chat & Call)
                              <Tooltip title={getColumnInfo('totalFailed')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#1976d2', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().totalCompleted && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#e3f2fd', color: '#1976d2' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total Completed (Chat & Call)
                              <Tooltip title={getColumnInfo('totalCompleted')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#1976d2', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().totalPaidCompleted && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#e3f2fd', color: '#1976d2' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total Paid Completed (Chat & Call)
                              <Tooltip title={getColumnInfo('totalPaidCompleted')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#1976d2', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().newUserRegistrations && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#e3f2fd', color: '#1976d2' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              New User Registrations
                              <Tooltip title={getColumnInfo('newUserRegistrations')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#1976d2', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}

                        {/* Chat Columns Sub-columns */}
                        {getVisibleColumnsForCurrentTab().totalAttemptsChats && (
                          <th style={{ border: '1px solid #e0e0e0', borderLeft: '2px solid #333', backgroundColor: '#c8e6c9', color: '#2e7d32' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total Attempt (Chat)
                              <Tooltip title={getColumnInfo('totalAttemptsChats')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#2e7d32', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().totalFailedChats && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#c8e6c9', color: '#2e7d32' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total Failed (Chat)
                              <Tooltip title={getColumnInfo('totalFailedChats')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#2e7d32', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().totalCompletedChats && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#c8e6c9', color: '#2e7d32' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total Completed (Chat)
                              <Tooltip title={getColumnInfo('totalCompletedChats')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#2e7d32', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().totalFreeChats && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#c8e6c9', color: '#2e7d32' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total Free Chat
                              <Tooltip title={getColumnInfo('totalFreeChats')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#2e7d32', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().totalPaidChats && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#c8e6c9', color: '#2e7d32' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total Paid Chat
                              <Tooltip title={getColumnInfo('totalPaidChats')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#2e7d32', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().totalFirstChats && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#c8e6c9', color: '#2e7d32' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total 1st Paid Chat
                              <Tooltip title={getColumnInfo('totalFirstChats')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#2e7d32', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                          {getVisibleColumnsForCurrentTab().totalRepeatChats && (
                          <th style={{ border: '1px solid #e0e0e0', borderRight: '2px solid #333', backgroundColor: '#c8e6c9', color: '#2e7d32' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total Repeat Paid Chat
                              <Tooltip title={getColumnInfo('totalRepeatChats')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#2e7d32', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}

                        {/* Call Columns Sub-columns */}
                        {getVisibleColumnsForCurrentTab().totalAttemptsCalls && (
                          <th style={{ border: '1px solid #e0e0e0', borderLeft: '2px solid #333', backgroundColor: '#ffe0b2', color: '#f57c00' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total Attempt (Call)
                              <Tooltip title={getColumnInfo('totalAttemptsCalls')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#f57c00', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().totalFailedCalls && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#ffe0b2', color: '#f57c00' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total Failed (Call)
                              <Tooltip title={getColumnInfo('totalFailedCalls')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#f57c00', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().totalCompletedCalls && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#ffe0b2', color: '#f57c00' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total Completed (Call)
                              <Tooltip title={getColumnInfo('totalCompletedCalls')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#f57c00', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().totalFreeCalls && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#ffe0b2', color: '#f57c00' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total Free Call
                              <Tooltip title={getColumnInfo('totalFreeCalls')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#f57c00', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().totalPaidCalls && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#ffe0b2', color: '#f57c00' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total Paid Call
                              <Tooltip title={getColumnInfo('totalPaidCalls')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#f57c00', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().totalFirstCalls && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#ffe0b2', color: '#f57c00' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total 1st Paid Call
                                    <Tooltip title={getColumnInfo('totalFirstCalls')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#f57c00', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().totalRepeatCalls && (
                          <th style={{ border: '1px solid #e0e0e0', borderRight: '2px solid #333', backgroundColor: '#ffe0b2', color: '#f57c00' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total Repeat Paid Call
                                <Tooltip title={getColumnInfo('totalRepeatCalls')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#f57c00', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}

                        {/* Time Spent (Chat + Call) Sub-columns */}
                        {getVisibleColumnsForCurrentTab().totalTime && (
                          <th style={{ border: '1px solid #e0e0e0', borderLeft: '2px solid #333', backgroundColor: '#e1bee7', color: '#7b1fa2' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total Time (Chat + Call)
                              <Tooltip title={getColumnInfo('totalTime')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#7b1fa2', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().avgTime && (
                          <th style={{ border: '1px solid #e0e0e0', borderRight: '2px solid #333', backgroundColor: '#e1bee7', color: '#7b1fa2' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Avg. Time (Chat + Call)
                              <Tooltip title={getColumnInfo('avgTime')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#7b1fa2', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {/* Chat (Time Spent) Sub-columns */}
                        {getVisibleColumnsForCurrentTab().totalTimeChats && (
                          <th style={{ border: '1px solid #e0e0e0', borderLeft: '2px solid #333', backgroundColor: '#c8e6c9', color: '#2e7d32' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total Time (Chat)
                              <Tooltip title={getColumnInfo('totalTimeChats')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#2e7d32', cursor: 'help' }} />    
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().avgTimeChats && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#c8e6c9', color: '#2e7d32' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>  
                              Avg. Time (Chat)
                              <Tooltip title={getColumnInfo('avgTimeChats')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#2e7d32', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th> 
                        )}
                        {/* Chat (Time Spent) Sub-columns */}
                        {getVisibleColumnsForCurrentTab().totalTimeFreeChat && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#c8e6c9', color: '#2e7d32' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total Time (Free Chat)
                              <Tooltip title={getColumnInfo('totalTimeFreeChat')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#2e7d32', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().avgTimeFreeChat && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#c8e6c9', color: '#2e7d32' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Avg. Time (Free Chat)
                              <Tooltip title={getColumnInfo('avgTimeFreeChat')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#2e7d32', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                       
                        {getVisibleColumnsForCurrentTab().totalTimePaidChat && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#c8e6c9', color: '#2e7d32' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total Time (Paid Chat)
                              <Tooltip title={getColumnInfo('totalTimePaidChat')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#2e7d32', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().avgTimePaidChat && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#c8e6c9', color: '#2e7d32' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Avg. Time (Paid Chat)
                              <Tooltip title={getColumnInfo('avgTimePaidChat')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#2e7d32', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().totalTimeFirstChat && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#c8e6c9', color: '#2e7d32' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total Time (1st Paid Chat)
                              <Tooltip title={getColumnInfo('totalTimeFirstChat')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#2e7d32', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().avgTimeFirstChat && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#c8e6c9', color: '#2e7d32' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Avg. Time (1st Paid Chat)
                              <Tooltip title={getColumnInfo('avgTimeFirstChat')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#2e7d32', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().totalTimeRepeatChats && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#c8e6c9', color: '#2e7d32' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total Time (Repeat Paid Chat)
                              <Tooltip title={getColumnInfo('totalTimeRepeatChats')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#2e7d32', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().avgTimeRepeatChats && (
                          <th style={{ border: '1px solid #e0e0e0', borderRight: '2px solid #333', backgroundColor: '#c8e6c9', color: '#2e7d32' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Avg. Time (Repeat Paid Chat)
                              <Tooltip title={getColumnInfo('avgTimeRepeatChats')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#2e7d32', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {
                          getVisibleColumnsForCurrentTab().totalTimeCalls && (
                            <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#ffe0b2', color: '#f57c00' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                Total Time (Call)
                              </Box>
                            </th>
                          )
                        }
                        {
                          getVisibleColumnsForCurrentTab().avgTimeCalls && (
                            <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#ffe0b2', color: '#f57c00' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                Avg. Time (Call)
                              </Box>
                            </th>
                          )
                        }

                        {/* Call (Time Spent) Sub-columns */}
                        {getVisibleColumnsForCurrentTab().totalTimeFreeCalls && (
                          <th style={{ border: '1px solid #e0e0e0', borderLeft: '2px solid #333', backgroundColor: '#ffe0b2', color: '#f57c00' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total Time (Free Call)
                              <Tooltip title={getColumnInfo('totalTimeFreeCalls')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#f57c00', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().avgTimeFreeCalls && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#ffe0b2', color: '#f57c00' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Avg. Time (Free Call)
                              <Tooltip title={getColumnInfo('avgTimeFreeCalls')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#f57c00', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().totalTimePaidCalls && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#ffe0b2', color: '#f57c00' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total Time (Paid Call)
                              <Tooltip title={getColumnInfo('totalTimePaidCalls')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#f57c00', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().avgTimePaidCalls   && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#ffe0b2', color: '#f57c00' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Avg. Time (Paid Call)
                              <Tooltip title={getColumnInfo('avgTimePaidCalls')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#f57c00', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().totalTimeFirstCalls && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#ffe0b2', color: '#f57c00' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total Time (1st Paid Call)
                              <Tooltip title={getColumnInfo('totalTimeFirstCalls')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#f57c00', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().avgTimeFirstCalls && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#ffe0b2', color: '#f57c00' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Avg. Time (1st Paid Call)
                              <Tooltip title={getColumnInfo('avgTimeFirstCalls')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#f57c00', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().totalTimeRepeatCalls && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#ffe0b2', color: '#f57c00' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total Time (Repeat Paid Call)
                              <Tooltip title={getColumnInfo('totalTimeRepeatCalls')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#f57c00', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                            {getVisibleColumnsForCurrentTab().avgTimeRepeatCalls && (
                          <th style={{ border: '1px solid #e0e0e0', borderRight: '2px solid #333', backgroundColor: '#ffe0b2', color: '#f57c00' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Average Time Repeat Calls
                              <Tooltip title={getColumnInfo('avgTimeRepeatCalls')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#f57c00', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        
                        {/* Money Spent (Chat + Call) Sub-columns */}
                        {getVisibleColumnsForCurrentTab().totalSpending && (
                          <th style={{ border: '1px solid #e0e0e0', borderLeft: '2px solid #333', backgroundColor: '#ECEDB0', color: '#93960b' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total Spent (Chat + Call)
                              <Tooltip title={getColumnInfo('totalSpending')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#93960b', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().avgSpending && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#ECEDB0', color: '#93960b' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Avg. Spent (Chat + Call)
                              <Tooltip title={getColumnInfo('avgSpending')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#93960b', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().totalRecharge && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#ECEDB0', color: '#93960b' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              No. of Recharge
                              <Tooltip title={getColumnInfo('totalRecharge')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#93960b', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().uniqueRechargeUsers && (
                          <th style={{ border: '1px solid #e0e0e0', borderRight: '2px solid #333', backgroundColor: '#ECEDB0', color: '#93960b' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Unique Recharge Users
                              <Tooltip title={getColumnInfo('uniqueRechargeUsers')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#93960b', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}

                        {/* Chat (Money Spent) Sub-columns */}
                        {getVisibleColumnsForCurrentTab().totalPaidChatSpending && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#c8e6c9', color: '#2e7d32' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total Spent (Paid Chat)
                              <Tooltip title={getColumnInfo('totalPaidChatSpending')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#2e7d32', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().avgPaidChatSpending && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#c8e6c9', color: '#2e7d32' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Avg. Spent (Paid Chat)
                              <Tooltip title={getColumnInfo('avgPaidChatSpending')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#2e7d32', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().totalFirstPaidChatSpending && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#c8e6c9', color: '#2e7d32' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total Spent (1st Paid Chat)
                              <Tooltip title={getColumnInfo('totalFirstPaidChatSpending')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#2e7d32', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                            {getVisibleColumnsForCurrentTab().avgFirstPaidChatSpending && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#c8e6c9', color: '#2e7d32' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Avg. Spent (1st Paid Chat)
                              <Tooltip title={getColumnInfo('avgSpentFirstPaidChat')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#2e7d32', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().totalRepeatPaidChatSpending  && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#c8e6c9', color: '#2e7d32' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total Spent (Repeat Paid Chat)
                              <Tooltip title={getColumnInfo('totalRepeatPaidChatSpending')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#2e7d32', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().avgRepeatPaidChatSpending && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#c8e6c9', color: '#2e7d32' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Avg. Spent (Repeat Paid Chat)
                              <Tooltip title={getColumnInfo('avgRepeatPaidChatSpending')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#2e7d32', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}

                        {/* Call (Money Spent) Sub-columns */}
                        {getVisibleColumnsForCurrentTab().totalPaidCallsSpending && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#ffe0b2', color: '#f57c00' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total Spent (Paid Call)
                              <Tooltip title={getColumnInfo('totalPaidCallsSpending')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#f57c00', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().avgPaidCallsSpending && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#ffe0b2', color: '#f57c00' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Avg. Spent (Paid Call)
                              <Tooltip title={getColumnInfo('avgPaidCallsSpending')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#f57c00', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().totalFirstPaidCallsSpending && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#ffe0b2', color: '#f57c00' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total Spent (1st Paid Call)
                              <Tooltip title={getColumnInfo('totalFirstPaidCallsSpending')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#f57c00', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                        {getVisibleColumnsForCurrentTab().avgFirstPaidCallsSpending && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#ffe0b2', color: '#f57c00' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Avg. Spent (1st Paid Call)
                                <Tooltip title={getColumnInfo('avgFirstPaidCallsSpending')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#f57c00', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                            {getVisibleColumnsForCurrentTab().totalRepeatPaidCallsSpending && (
                          <th style={{ border: '1px solid #e0e0e0', backgroundColor: '#ffe0b2', color: '#f57c00' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Total Spent (Repeat Paid Call)
                              <Tooltip title={getColumnInfo('totalRepeatPaidCallsSpending')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#f57c00', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                          {getVisibleColumnsForCurrentTab().avgRepeatPaidCallsSpending && (
                          <th style={{ border: '1px solid #e0e0e0', borderRight: '2px solid #333', backgroundColor: '#ffe0b2', color: '#f57c00' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              Avg. Spent (Repeat Paid Call)
                              <Tooltip title={getColumnInfo('avgRepeatPaidCallsSpending')} arrow>
                                <InfoIcon sx={{ fontSize: 16, color: '#f57c00', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {userList.length > 0 ? (
                        <>
                          {/* Totals Row */}
                         <tr style={{
                           backgroundColor: '#f5f5f5', 
                           fontWeight: 'bold', 
                           borderTop: '2px solid #333',
                           position: 'sticky',
                           bottom: 0,
                           zIndex: 9,
                           boxShadow: '0 -2px 4px rgba(0,0,0,0.1)'
                         }}>
                            <td style={{
                              textAlign: 'center', 
                              fontWeight: 'bold',
                              position: 'sticky',
                              left: 0,
                              zIndex: 10,
                              backgroundColor: '#f5f5f5',
                              boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                            }}>TOTAL</td>
                            
                            {getVisibleColumnsForCurrentTab().date && <td></td>}
                            {getVisibleColumnsForCurrentTab().consultantType && <td></td>}
                            {getVisibleColumnsForCurrentTab().problemType && <td></td>}
                            {getVisibleColumnsForCurrentTab().consultantGender && <td></td>}
                            {getVisibleColumnsForCurrentTab().userGender && <td></td>}
                            
                            {/* Numerical Data (Total Chat + Call) - Matching header colors */}
                            {getVisibleColumnsForCurrentTab().totalAttempts && <td style={{backgroundColor: '#e3f2fd', color: '#1976d2', fontWeight: 'bold'}}>{totals.totalAttempts || 0}</td>}
                            {getVisibleColumnsForCurrentTab().totalFailed && <td style={{backgroundColor: '#e3f2fd', color: '#1976d2', fontWeight: 'bold'}}>{totals.totalFailed || 0}</td>}
                            {getVisibleColumnsForCurrentTab().totalCompleted && <td style={{backgroundColor: '#e3f2fd', color: '#1976d2', fontWeight: 'bold'}}>{totals.totalCompleted || 0}</td>}
                            {getVisibleColumnsForCurrentTab().totalPaidCompleted && <td style={{backgroundColor: '#e3f2fd', color: '#1976d2', fontWeight: 'bold'}}>{totals.totalPaidChats + totals.totalPaidCalls || 0}</td>}
                            {getVisibleColumnsForCurrentTab().newUserRegistrations && <td style={{backgroundColor: '#e3f2fd', color: '#1976d2', fontWeight: 'bold'}}>{totals.newUserRegistrations || 0}</td>}
                            
                            {/* Chat Columns - Matching header colors */}
                            {getVisibleColumnsForCurrentTab().totalAttemptsChats && <td style={{backgroundColor: '#c8e6c9', color: '#2e7d32', fontWeight: 'bold', borderLeft: '2px solid #333'}}>{totals.totalAttemptsChats || 0}</td>}
                            {getVisibleColumnsForCurrentTab().totalFailedChats && <td style={{backgroundColor: '#c8e6c9', color: '#2e7d32', fontWeight: 'bold'}}>{totals.totalFailedChats || 0}</td>}
                            {getVisibleColumnsForCurrentTab().failedAttemptsChats && <td style={{backgroundColor: '#c8e6c9', color: '#2e7d32', fontWeight: 'bold'}}>{totals.failedAttemptsChats || 0}</td>}
                            {getVisibleColumnsForCurrentTab().totalCompletedChats && <td style={{backgroundColor: '#c8e6c9', color: '#2e7d32', fontWeight: 'bold'}}>{totals.totalCompletedChats || 0}</td>}
                            {getVisibleColumnsForCurrentTab().totalFreeChats && <td style={{backgroundColor: '#c8e6c9', color: '#2e7d32', fontWeight: 'bold'}}>{totals.totalFreeChats || 0}</td>}
                            {getVisibleColumnsForCurrentTab().totalPaidChats && <td style={{backgroundColor: '#c8e6c9', color: '#2e7d32', fontWeight: 'bold'}}>{totals.totalPaidChats || 0}</td>}
                            {getVisibleColumnsForCurrentTab().totalFirstChats && <td style={{backgroundColor: '#c8e6c9', color: '#2e7d32', fontWeight: 'bold'}}>{totals.totalFirstChats || 0}</td>}
                            {getVisibleColumnsForCurrentTab().totalRepeatChats && <td style={{backgroundColor: '#c8e6c9', color: '#2e7d32', fontWeight: 'bold', borderRight: '2px solid #333'}}>{totals.totalRepeatChats || 0}</td>}
                            
                            {/* Call Columns - Matching header colors */}
                            {getVisibleColumnsForCurrentTab().totalAttemptsCalls && <td style={{backgroundColor: '#ffe0b2', color: '#f57c00', fontWeight: 'bold', borderLeft: '2px solid #333'}}>{totals.totalAttemptsCalls || 0}</td>}
                            {getVisibleColumnsForCurrentTab().totalFailedCalls && <td style={{backgroundColor: '#ffe0b2', color: '#f57c00', fontWeight: 'bold'}}>{totals.totalFailedCalls || 0}</td>}
                            {getVisibleColumnsForCurrentTab().failedAttemptsCalls && <td style={{backgroundColor: '#ffe0b2', color: '#f57c00', fontWeight: 'bold'}}>{totals.failedAttemptsCalls || 0}</td>}
                            {getVisibleColumnsForCurrentTab().totalCompletedCalls && <td style={{backgroundColor: '#ffe0b2', color: '#f57c00', fontWeight: 'bold'}}>{totals.totalCompletedCalls || 0}</td>}
                            {getVisibleColumnsForCurrentTab().totalFreeCalls && <td style={{backgroundColor: '#ffe0b2', color: '#f57c00', fontWeight: 'bold'}}>{totals.totalFreeCalls || 0}</td>}
                            {getVisibleColumnsForCurrentTab().totalPaidCalls && <td style={{backgroundColor: '#ffe0b2', color: '#f57c00', fontWeight: 'bold'}}>{totals.totalPaidCalls || 0}</td>}
                            {getVisibleColumnsForCurrentTab().totalFirstCalls && <td style={{backgroundColor: '#ffe0b2', color: '#f57c00', fontWeight: 'bold'}}>{totals.totalFirstCalls || 0}</td>}
                            {getVisibleColumnsForCurrentTab().totalRepeatCalls && <td style={{backgroundColor: '#ffe0b2', color: '#f57c00', fontWeight: 'bold', borderRight: '2px solid #333'}}>{totals.totalRepeatCalls || 0}</td>}
                            
                            {/* Time Spent (Chat + Call) - Matching header colors */}
                            {getVisibleColumnsForCurrentTab().totalTime && <td style={{backgroundColor: '#e1bee7', color: '#7b1fa2', fontWeight: 'bold', borderLeft: '2px solid #333'}}>{formatTime(totals.totalTime || 0)}</td>}
                            {getVisibleColumnsForCurrentTab().avgTime && <td style={{backgroundColor: '#e1bee7', color: '#7b1fa2', fontWeight: 'bold', borderRight: '2px solid #333'}}>{formatTime(totals.avgTime || 0)}</td>}
                            
                            {/* Chat (Time Spent) - Matching header colors */}
                            {getVisibleColumnsForCurrentTab().totalTimeChats && <td style={{backgroundColor: '#c8e6c9', color: '#2e7d32', fontWeight: 'bold', borderLeft: '2px solid #333'}}>{formatTime(totals.totalTimeChats || 0)}</td>}
                            {getVisibleColumnsForCurrentTab().avgTimeChats && <td style={{backgroundColor: '#c8e6c9', color: '#2e7d32', fontWeight: 'bold'}}>{formatTime(totals.avgTimeChats || 0)}</td>}
                            {getVisibleColumnsForCurrentTab().totalTimeFreeChat && <td style={{backgroundColor: '#c8e6c9', color: '#2e7d32', fontWeight: 'bold'}}>{formatTime(totals.totalTimeFreeChat || 0)}</td>}
                            {getVisibleColumnsForCurrentTab().avgTimeFreeChat && <td style={{backgroundColor: '#c8e6c9', color: '#2e7d32', fontWeight: 'bold'}}>{formatTime(totals.avgTimeFreeChat || 0)}</td>}
                            {getVisibleColumnsForCurrentTab().totalTimePaidChat && <td style={{backgroundColor: '#c8e6c9', color: '#2e7d32', fontWeight: 'bold'}}>{formatTime(totals.totalTimePaidChat || 0)}</td>}
                            {getVisibleColumnsForCurrentTab().avgTimePaidChat && <td style={{backgroundColor: '#c8e6c9', color: '#2e7d32', fontWeight: 'bold'}}>{formatTime(totals.avgTimePaidChat || 0)}</td>}
                            {getVisibleColumnsForCurrentTab().totalTimeFirstChat && <td style={{backgroundColor: '#c8e6c9', color: '#2e7d32', fontWeight: 'bold'}}>{formatTime(totals.totalTimeFirstChat || 0)}</td>}
                            {getVisibleColumnsForCurrentTab().avgTimeFirstChat && <td style={{backgroundColor: '#c8e6c9', color: '#2e7d32', fontWeight: 'bold'}}>{formatTime(totals.avgTimeFirstChat || 0)}</td>}
                            {getVisibleColumnsForCurrentTab().totalTimeRepeatChats && <td style={{backgroundColor: '#c8e6c9', color: '#2e7d32', fontWeight: 'bold'}}>{formatTime(totals.totalTimeRepeatChats || 0)}</td>}
                            {getVisibleColumnsForCurrentTab().avgTimeRepeatChats && <td style={{backgroundColor: '#c8e6c9', color: '#2e7d32', fontWeight: 'bold', borderRight: '2px solid #333'}}>{formatTime(totals.avgTimeRepeatChats || 0)}</td>}
                            
                            {/* Call (Time Spent) - Matching header colors */}
                            {getVisibleColumnsForCurrentTab().totalTimeCalls && <td style={{backgroundColor: '#ffe0b2', color: '#f57c00', fontWeight: 'bold', borderLeft: '2px solid #333'}}>{formatTime(totals.totalTimeCalls || 0)}</td>}
                            {getVisibleColumnsForCurrentTab().avgTimeCalls && <td style={{backgroundColor: '#ffe0b2', color: '#f57c00', fontWeight: 'bold'}}>{formatTime(totals.avgTimeCalls || 0)}</td>}
                            {getVisibleColumnsForCurrentTab().totalTimeFreeCalls && <td style={{backgroundColor: '#ffe0b2', color: '#f57c00', fontWeight: 'bold'}}>{formatTime(totals.totalTimeFreeCalls || 0)}</td>}
                            {getVisibleColumnsForCurrentTab().avgTimeFreeCalls && <td style={{backgroundColor: '#ffe0b2', color: '#f57c00', fontWeight: 'bold'}}>{formatTime(totals.avgTimeFreeCalls || 0)}</td>}
                            {getVisibleColumnsForCurrentTab().totalTimePaidCalls && <td style={{backgroundColor: '#ffe0b2', color: '#f57c00', fontWeight: 'bold'}}>{formatTime(totals.totalTimePaidCalls || 0)}</td>}
                            {getVisibleColumnsForCurrentTab().avgTimePaidCalls && <td style={{backgroundColor: '#ffe0b2', color: '#f57c00', fontWeight: 'bold'}}>{formatTime(totals.avgTimePaidCalls || 0)}</td>}
                            {getVisibleColumnsForCurrentTab().totalTimeFirstCalls && <td style={{backgroundColor: '#ffe0b2', color: '#f57c00', fontWeight: 'bold'}}>{formatTime(totals.totalTimeFirstCalls || 0)}</td>}
                            {getVisibleColumnsForCurrentTab().avgTimeFirstCalls && <td style={{backgroundColor: '#ffe0b2', color: '#f57c00', fontWeight: 'bold'}}>{formatTime(totals.avgTimeFirstCalls || 0)}</td>}
                            {getVisibleColumnsForCurrentTab().totalTimeRepeatCalls && <td style={{backgroundColor: '#ffe0b2', color: '#f57c00', fontWeight: 'bold'}}>{formatTime(totals.totalTimeRepeatCalls || 0)}</td>}
                            {getVisibleColumnsForCurrentTab().avgTimeRepeatCalls && <td style={{backgroundColor: '#ffe0b2', color: '#f57c00', fontWeight: 'bold', borderRight: '2px solid #333'}}>{formatTime(totals.avgTimeRepeatCalls || 0)}</td>}
                            
                            {/* Money Spent (Chat + Call) - Matching header colors */}
                            {getVisibleColumnsForCurrentTab().totalSpending && <td style={{backgroundColor: '#ECEDB0', color: '#93960b', fontWeight: 'bold', borderLeft: '2px solid #333'}}>{formatCurrency(totals.totalSpending || 0)}</td>}
                            {getVisibleColumnsForCurrentTab().avgSpending && <td style={{backgroundColor: '#ECEDB0', color: '#93960b', fontWeight: 'bold'}}>{formatCurrency(totals.avgSpending || 0)}</td>}
                            {getVisibleColumnsForCurrentTab().totalRecharge && <td style={{backgroundColor: '#ECEDB0', color: '#93960b', fontWeight: 'bold'}}>{totals.totalRecharge || 0}</td>}
                            {getVisibleColumnsForCurrentTab().uniqueRechargeUsers && <td style={{backgroundColor: '#ECEDB0', color: '#93960b', fontWeight: 'bold', borderRight: '2px solid #333'}}>{totals.uniqueRechargeUsers || 0}</td>}
                            
                            {/* Chat (Money Spent) - Matching header colors */}
                            {getVisibleColumnsForCurrentTab().totalPaidChatSpending && <td style={{backgroundColor: '#c8e6c9', color: '#2e7d32', fontWeight: 'bold', borderLeft: '2px solid #333'}}>{formatCurrency(totals.totalPaidChatSpending || 0)}</td>}
                            {getVisibleColumnsForCurrentTab().avgPaidChatSpending && <td style={{backgroundColor: '#c8e6c9', color: '#2e7d32', fontWeight: 'bold'}}>{formatCurrency(totals.avgPaidChatSpending || 0)}</td>}
                            {getVisibleColumnsForCurrentTab().totalFirstPaidChatSpending && <td style={{backgroundColor: '#c8e6c9', color: '#2e7d32', fontWeight: 'bold'}}>{formatCurrency(totals.totalFirstPaidChatSpending || 0)}</td>}
                            {getVisibleColumnsForCurrentTab().avgFirstPaidChatSpending && <td style={{backgroundColor: '#c8e6c9', color: '#2e7d32', fontWeight: 'bold'}}>{formatCurrency(totals.avgFirstPaidChatSpending || 0)}</td>}
                            {getVisibleColumnsForCurrentTab().totalRepeatPaidChatSpending && <td style={{backgroundColor: '#c8e6c9', color: '#2e7d32', fontWeight: 'bold'}}>{formatCurrency(totals.totalRepeatPaidChatSpending || 0)}</td>}
                            {getVisibleColumnsForCurrentTab().avgRepeatPaidChatSpending && <td style={{backgroundColor: '#c8e6c9', color: '#2e7d32', fontWeight: 'bold', borderRight: '2px solid #333'}}>{formatCurrency(totals.avgRepeatPaidChatSpending || 0)}</td>}
                            
                            {/* Call (Money Spent) - Matching header colors */}
                            {getVisibleColumnsForCurrentTab().totalPaidCallsSpending && <td style={{backgroundColor: '#ffe0b2', color: '#f57c00', fontWeight: 'bold', borderLeft: '2px solid #333'}}>{formatCurrency(totals.totalPaidCallsSpending || 0)}</td>}
                            {getVisibleColumnsForCurrentTab().avgPaidCallsSpending && <td style={{backgroundColor: '#ffe0b2', color: '#f57c00', fontWeight: 'bold'}}>{formatCurrency(totals.avgPaidCallsSpending || 0)}</td>}
                            {getVisibleColumnsForCurrentTab().totalFirstPaidCallsSpending && <td style={{backgroundColor: '#ffe0b2', color: '#f57c00', fontWeight: 'bold'}}>{formatCurrency(totals.totalFirstPaidCallsSpending || 0)}</td>}
                            {getVisibleColumnsForCurrentTab().avgFirstPaidCallsSpending && <td style={{backgroundColor: '#ffe0b2', color: '#f57c00', fontWeight: 'bold'}}>{formatCurrency(totals.avgFirstPaidCallsSpending || 0)}</td>}
                            {getVisibleColumnsForCurrentTab().totalRepeatPaidCallsSpending && <td style={{backgroundColor: '#ffe0b2', color: '#f57c00', fontWeight: 'bold'}}>{formatCurrency(totals.totalRepeatPaidCallsSpending || 0)}</td>}
                            {getVisibleColumnsForCurrentTab().avgRepeatPaidCallsSpending && <td style={{backgroundColor: '#ffe0b2', color: '#f57c00', fontWeight: 'bold', borderRight: '2px solid #333'}}>{formatCurrency(totals.avgRepeatPaidCallsSpending || 0)}</td>}
                          </tr> 
                          {userList.map((item, index) => (
                            <tr key={item._id} style={getRowStyle(index)}>
                              <td style={{ 
                                border: '1px solid #e0e0e0',
                                position: 'sticky',
                                left: 0,
                                zIndex: 8,
                                backgroundColor: getRowStyle(index).backgroundColor || 'white',
                                boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                              }}>{findSerialNumber(index, activePage, pageSize)}</td>
                              
                              {/* Date Information */}
                              {getVisibleColumnsForCurrentTab().date && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#f8f9fa' }}>{moment(item.date).format('DD MMM YYYY')}</td>}
                              {getVisibleColumnsForCurrentTab().consultantType && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#f8f9fa' }}>{formatFilterValue(consultantType, 'consultantType')}</td>}
                              {getVisibleColumnsForCurrentTab().problemType && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#f8f9fa' }}>{formatFilterValue(problemType, 'problemType')}</td>}
                              {getVisibleColumnsForCurrentTab().consultantGender && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#f8f9fa' }}>{formatFilterValue(consultantGender, 'gender')}</td>}
                              {getVisibleColumnsForCurrentTab().userGender && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#f8f9fa' }}>{formatFilterValue(userGender, 'gender')}</td>}
                              
                              {/* Numerical Data (Total Chat + Call) */}
                              {getVisibleColumnsForCurrentTab().totalAttempts && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#e3f2fd' }}>{item.totalAttempts || 0}</td>}
                                {getVisibleColumnsForCurrentTab().totalFailed && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#e3f2fd' }}>{(item.totalAttempts - item.totalCompleted) || 0}</td>}
                              {getVisibleColumnsForCurrentTab().totalCompleted && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#e3f2fd' }}>{item.totalCompleted || 0}</td>}
                              {getVisibleColumnsForCurrentTab().totalPaidCompleted && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#e3f2fd' }}>{item.totalPaidChats + item.totalPaidCalls || 0}</td>}
                              {getVisibleColumnsForCurrentTab().newUserRegistrations && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#e3f2fd' }}>{item.newUserRegistrations || 0}</td>}

                              {/* Chat Columns */}
                              {getVisibleColumnsForCurrentTab().totalAttemptsChats && <td style={{ border: '1px solid #e0e0e0', borderLeft: '2px solid #333', backgroundColor: '#e8f5e8' }}>{item.totalAttemptsChats || 0}</td>}
                              {getVisibleColumnsForCurrentTab().totalFailedChats && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#e8f5e8' }}>{item.totalFailedChats || 0}</td>}
                              {getVisibleColumnsForCurrentTab().totalCompletedChats && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#e8f5e8' }}>{item.totalCompletedChats     || 0}</td>}
                              {getVisibleColumnsForCurrentTab().totalFreeChats && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#e8f5e8' }}>{item.totalFreeChats || 0}</td>}
                              {getVisibleColumnsForCurrentTab().totalPaidChats && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#e8f5e8' }}>{item.totalPaidChats || 0}</td>}
                              {getVisibleColumnsForCurrentTab().totalFirstChats && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#e8f5e8' }}>{item.totalFirstChats || 0}</td>}
                              {getVisibleColumnsForCurrentTab().totalRepeatChats && <td style={{ border: '1px solid #e0e0e0', borderRight: '2px solid #333', backgroundColor: '#e8f5e8' }}>{item.totalRepeatChats || 0}</td>}   

                              {/* Call Columns */}
                              {getVisibleColumnsForCurrentTab().totalAttemptsCalls && <td style={{ border: '1px solid #e0e0e0', borderLeft: '2px solid #333', backgroundColor: '#fff3e0' }}>{item.totalAttemptsCalls || 0}</td>}
                              {getVisibleColumnsForCurrentTab().totalFailedCalls && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#fff3e0' }}>{item.totalFailedCalls || 0}</td>}
                              {getVisibleColumnsForCurrentTab().totalCompletedCalls && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#fff3e0' }}>{item.totalCompletedCalls || 0}</td>}
                                {getVisibleColumnsForCurrentTab().totalFreeCalls && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#fff3e0' }}>{item.totalFreeCalls || 0}</td>}
                              {getVisibleColumnsForCurrentTab().totalPaidCalls && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#fff3e0' }}>{item.totalPaidCalls || 0}</td>}
                              {getVisibleColumnsForCurrentTab().totalFirstCalls && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#fff3e0' }}>{item.totalFirstCalls || 0}</td>}
                              {getVisibleColumnsForCurrentTab().totalRepeatCalls && <td style={{ border: '1px solid #e0e0e0', borderRight: '2px solid #333', backgroundColor: '#fff3e0' }}>{item.totalRepeatCalls || 0}</td>} 

                              {/* Time Spent (Chat + Call) */}
                              {getVisibleColumnsForCurrentTab().totalTime && <td style={{ border: '1px solid #e0e0e0', borderLeft: '2px solid #333', backgroundColor: '#f3e5f5' }}>{formatTime(item.totalTime || 0)}</td>}
                              {getVisibleColumnsForCurrentTab().avgTime && <td style={{ border: '1px solid #e0e0e0', borderRight: '2px solid #333', backgroundColor: '#f3e5f5' }}>{formatTime(item.avgTime || 0)}</td>}

                              {/* Chat (Time Spent) */}
                              {getVisibleColumnsForCurrentTab().totalTimeChats && <td style={{ border: '1px solid #e0e0e0', borderLeft: '2px solid #333', backgroundColor: '#e8f5e8' }}>{formatTime(item.totalTimeChats || 0)}</td>}
                              {getVisibleColumnsForCurrentTab().avgTimeChats && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#e8f5e8' }}>{formatTime(item.avgTimeChats || 0)}</td>}
                              {getVisibleColumnsForCurrentTab().totalTimeFreeChat && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#e8f5e8' }}>{formatTime(item.totalTimeFreeChat || 0)}</td>}
                              {getVisibleColumnsForCurrentTab().avgTimeFreeChat && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#e8f5e8' }}>{formatTime(item.avgTimeFreeChat || 0)}</td>}
                              {getVisibleColumnsForCurrentTab().totalTimePaidChat && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#e8f5e8' }}>{formatTime(item.totalTimePaidChat || 0)}</td>}
                              {getVisibleColumnsForCurrentTab().avgTimePaidChat && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#e8f5e8' }}>{formatTime(item.avgTimePaidChat || 0)}</td>}
                              {getVisibleColumnsForCurrentTab().totalTimeFirstChat && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#e8f5e8' }}>{formatTime(item.totalTimeFirstChat || 0)}</td>}
                              {getVisibleColumnsForCurrentTab().avgTimeFirstChat && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#e8f5e8' }}>{formatTime(item.avgTimeFirstChat || 0)}</td>}
                              {getVisibleColumnsForCurrentTab().totalTimeRepeatChats && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#e8f5e8' }}>{formatTime(item.totalTimeRepeatChats || 0)}</td>}
                              {getVisibleColumnsForCurrentTab().avgTimeRepeatChats && <td style={{ border: '1px solid #e0e0e0', borderRight: '2px solid #333', backgroundColor: '#e8f5e8' }}>{formatTime(item.avgTimeRepeatChats || 0)}</td>}

                              {/* Call (Time Spent) */}
                              {getVisibleColumnsForCurrentTab().totalTimeCalls && <td style={{ border: '1px solid #e0e0e0', borderLeft: '2px solid #333', backgroundColor: '#fff3e0' }}>{formatTime(item.totalTimeCalls || 0)}</td>}
                              {getVisibleColumnsForCurrentTab().avgTimeCalls && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#fff3e0' }}>{formatTime(item.avgTimeCalls || 0)}</td>}
                              {getVisibleColumnsForCurrentTab().totalTimeFreeCalls && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#fff3e0' }}>{formatTime(item.totalTimeFreeCalls || 0)}</td>}
                              {getVisibleColumnsForCurrentTab().avgTimeFreeCalls && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#fff3e0' }}>{formatTime(item.avgTimeFreeCalls || 0)}</td>}
                              {getVisibleColumnsForCurrentTab().totalTimePaidCalls && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#fff3e0' }}>{formatTime(item.totalTimePaidCalls || 0)}</td>}
                              {getVisibleColumnsForCurrentTab().avgTimePaidCalls && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#fff3e0' }}>{formatTime(item.avgTimePaidCalls || 0)}</td>}
                              {getVisibleColumnsForCurrentTab().totalTimeFirstCalls && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#fff3e0' }}>{formatTime(item.totalTimeFirstCalls || 0)}</td>}
                              {getVisibleColumnsForCurrentTab().avgTimeFirstCalls && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#fff3e0' }}>{formatTime(item.avgTimeFirstCalls || 0)}</td>}
                              {getVisibleColumnsForCurrentTab().totalTimeRepeatCalls && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#fff3e0' }}>{formatTime(item.totalTimeRepeatCalls || 0)}</td>}
                              {getVisibleColumnsForCurrentTab().avgTimeRepeatCalls && <td style={{ border: '1px solid #e0e0e0', borderRight: '2px solid #333', backgroundColor: '#fff3e0' }}>{formatTime(item.avgTimeRepeatCalls || 0)}</td>}
                              
                              {/* Money Spent (Chat + Call) */}
                              {getVisibleColumnsForCurrentTab().totalSpending && <td style={{ border: '1px solid #e0e0e0', borderLeft: '2px solid #333', backgroundColor: '#ECEDB0' }}>{formatCurrency(item.totalSpending || 0)}</td>}
                              {getVisibleColumnsForCurrentTab().avgSpending && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#ECEDB0' }}>{formatCurrency(item.avgSpending || 0)}</td>}
                              {getVisibleColumnsForCurrentTab().totalRecharge && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#ECEDB0' }}>{item.totalRecharge || 0}</td>}
                              {getVisibleColumnsForCurrentTab().uniqueRechargeUsers && <td style={{ border: '1px solid #e0e0e0', borderRight: '2px solid #333', backgroundColor: '#ECEDB0' }}>{item.uniqueRechargeUsers || 0}</td>}

                              {/* Chat (Money Spent) */}
                              {getVisibleColumnsForCurrentTab().totalPaidChatSpending && <td style={{ border: '1px solid #e0e0e0', borderLeft: '2px solid #333', backgroundColor: '#e8f5e8' }}>{formatCurrency(item.totalPaidChatSpending || 0)}</td>}
                              {getVisibleColumnsForCurrentTab().avgPaidChatSpending && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#e8f5e8' }}>{formatCurrency(item.avgPaidChatSpending || 0)}</td>}
                              {getVisibleColumnsForCurrentTab().totalFirstPaidChatSpending && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#e8f5e8' }}>{formatCurrency(item.totalFirstPaidChatSpending || 0)}</td>}
                              {getVisibleColumnsForCurrentTab().avgFirstPaidChatSpending && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#e8f5e8' }}>{formatCurrency(item.avgFirstPaidChatSpending || 0)}</td>}
                              {getVisibleColumnsForCurrentTab().totalRepeatPaidChatSpending && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#e8f5e8' }}>{formatCurrency(item.totalRepeatPaidChatSpending || 0)}</td>}
                              {getVisibleColumnsForCurrentTab().avgRepeatPaidChatSpending && <td style={{ border: '1px solid #e0e0e0', borderRight: '2px solid #333', backgroundColor: '#e8f5e8' }}>{formatCurrency(item.avgRepeatPaidChatSpending || 0)}</td>}

                              {/* Call (Money Spent) */}
                              {getVisibleColumnsForCurrentTab().totalPaidCallsSpending && <td style={{ border: '1px solid #e0e0e0', borderLeft: '2px solid #333', backgroundColor: '#fff3e0' }}>{formatCurrency(item.totalPaidCallsSpending || 0)}</td>}
                              {getVisibleColumnsForCurrentTab().avgPaidCallsSpending && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#fff3e0' }}>{formatCurrency(item.avgPaidCallsSpending || 0)}</td>}
                              {getVisibleColumnsForCurrentTab().totalFirstPaidCallsSpending && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#fff3e0' }}>{formatCurrency(item.totalFirstPaidCallsSpending || 0)}</td>}
                              {getVisibleColumnsForCurrentTab().avgFirstPaidCallsSpending && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#fff3e0' }}>{formatCurrency(item.avgFirstPaidCallsSpending || 0)}</td>}
                              {getVisibleColumnsForCurrentTab().totalRepeatPaidCallsSpending && <td style={{ border: '1px solid #e0e0e0', backgroundColor: '#fff3e0' }}>{formatCurrency(item.totalRepeatPaidCallsSpending || 0)}</td>}
                              {getVisibleColumnsForCurrentTab().avgRepeatPaidCallsSpending && <td style={{ border: '1px solid #e0e0e0', borderRight: '2px solid #333', backgroundColor: '#fff3e0' }}>{formatCurrency(item.avgRepeatPaidCallsSpending || 0)}</td>}
                            </tr>
                          ))}
                          
                          {/* Spacer row for sticky totals */}
                          <tr style={{ height: '60px' }}>
                            <td style={{ 
                              border: 'none', 
                              backgroundColor: 'transparent',
                              position: 'sticky',
                              left: 0,
                              zIndex: 8
                            }}></td>
                            <td colSpan="99%" style={{ border: 'none', backgroundColor: 'transparent' }}></td>
                          </tr>
                        
                        </>
                      ) : (
                        <Box className="text-center py-4">
                          <Typography variant="h6" color="textSecondary">
                            {error ? 'Error loading data' : 'No data found for selected filters'}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" className="mt-2">
                            Try adjusting your filters or date range
                          </Typography>
                        </Box>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination and Page Size Controls */}
                <div className="pagination-wrapper mt-4 d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center gap-3">
                    <Typography variant="body2" color="textSecondary">
                      Show:
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 80 }}>
                      <Select
                        value={pageSize}
                        onChange={(e) => handlePageSizeChange(e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                      </Select>
                    </FormControl>
                    <Typography variant="body2" color="textSecondary">
                      items per page
                    </Typography>
                  </div>
                  
                  <div className="d-flex align-items-center gap-2">
                    <Typography variant="body2" color="textSecondary">
                      Showing {((activePage - 1) * pageSize) + 1} to {Math.min(activePage * pageSize, total)} of {total} items
                    </Typography>
                  </div>
                  
                  {total > pageSize && (
                    <div className="Pagination">
                      <Pagination
                        activePage={activePage}
                        itemsCountPerPage={pageSize}
                        totalItemsCount={total}
                        pageRangeDisplayed={5}
                        onChange={handlePageChange}
                        itemClass="page-item"
                        linkClass="page-link"
                      />
                    </div>
                  )}
                </div>
             
              </Paper>

              {/* Column Selector Drawer */}
              <Drawer
                anchor="right"
                open={columnSelectorOpen}
                onClose={() => setColumnSelectorOpen(false)}
                PaperProps={{
                  sx: { width: 400 }
                }}
              >
                <Box sx={{ p: 3 }}>
                  <Typography variant="h6" className="mb-3">
                    Customize Columns
                  </Typography>
                  
                  {/* Global Actions */}
                  <Box className="mb-3">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleSelectAllColumns}
                      className="me-2"
                    >
                      Select All
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleDeselectAllColumns}
                    >
                      Deselect All
                    </Button>
                  </Box>
                  <Divider className="mb-3" />
                  
                  <Box className="d-flex justify-content-between align-items-center mb-3">
                    <Typography variant="body2" color="textSecondary">
                      {getVisibleColumnCount()} of {getColumnsForActiveTab().length} columns selected
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => setColumnSelectorOpen(false)}
                    >
                      Apply
                    </Button>
                  </Box>
                  <Divider className="mb-3" />

                  {/* Column Categories */}
                  {Object.entries(getAllColumnsByCategory()).map(([category, columns]) => (
                    <Box key={category} className="mb-4">
                      <Box className="d-flex justify-content-between align-items-center mb-2">
                        <Typography variant="subtitle1" fontWeight="bold">
                          {category}
                        </Typography>
                        <Box>
                          <Button
                            size="small"
                            onClick={() => handleSelectAllInCategory(category)}
                            className="me-1"
                          >
                            All
                          </Button>
                          <Button
                            size="small"
                            onClick={() => handleDeselectAllInCategory(category)}
                          >
                            None
                          </Button>
                        </Box>
                      </Box>
                      
                      <List dense>
                        {columns.map((column) => (
                          <ListItem key={column.key} dense>
                            <ListItemIcon>
                              <Checkbox
                                edge="start"
                                checked={visibleColumns[column.key]}
                                onChange={() => handleColumnToggle(column.key)}
                                size="small"
                              />
                            </ListItemIcon>
                            <ListItemText 
                              primary={column.label}
                              secondary={column.type}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  ))}

                 
                </Box>
              </Drawer>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UserInsight